import type { NullableGrantEntry } from "../../../../shared/rbac/roles";
import type { GrantEntry, RoleOption } from "../../../../shared/rbac/roles";
import { roleLabel, rolesForScope } from "../../../../shared/rbac/roles";
import type { SpaceMode } from "../../../../shared/schema";
import { defaultRoleForScope } from "../../../lib/defaultRoleForScope";
import {
  grantToRoleChip,
  type ResolveScopeName,
  type RoleChip,
} from "../../../lib/roleChip";
import { isPrincipalGrant, type AccessPrincipal } from "./buildScopeDesired";
import {
  inferScopeKind,
  isEditableScopeKind,
  type EditableScopeKind as ManageableKind,
} from "./inferScopeKind";

type RoleGrantRow = NullableGrantEntry;

export type PrincipalGrant = {
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

export type InheritedGrant = {
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
  customRole: { id: string; name: string; scopeKind: string } | null;
  viaGroupId: string;
  viaGroupName: string;
};

export type SpaceRow = {
  id: string;
  name: string;
  mode: SpaceMode;
  roleGrantsAsScope: readonly RoleGrantRow[];
};

export type AppRow = {
  id: string;
  name: string;
  identifier: string;
  roleGrantsAsScope: readonly RoleGrantRow[];
};

export type SkillRow = {
  id: string;
  name: string;
  roleGrantsAsScope: readonly RoleGrantRow[];
};

export type CustomRole = { id: string; name: string; scopeKind: string };

export type Scope = {
  scopeKind: ManageableKind;
  name: string;
  mode?: SpaceMode;
  identifier?: string;
  grants: readonly RoleGrantRow[];
};

export type Row = {
  scopeId: string;
  scopeKind: ManageableKind;
  name: string;
  mode?: SpaceMode;
  identifier?: string;
  directGrants: GrantEntry[];
  directRoleLabels: string[];
  roleOptions: RoleOption[];
  editable: boolean;
  inheritedChips: RoleChip[];
  viaGroups: { id: string; name: string }[];
};

export type StagedScope = {
  key: string;
  scopeId: string;
  scopeKind: ManageableKind;
  name: string;
  mode?: SpaceMode;
  identifier?: string;
  roleOptions: RoleOption[];
  roles: RoleOption[];
};

export type Match = { scopeId: string; scopeKind: ManageableKind };

const SCOPE_ORDER: Record<ManageableKind, number> = {
  space: 0,
  connected_app: 1,
  skill: 2,
};

export type ScopeCatalog = {
  spaces: readonly SpaceRow[];
  apps: readonly AppRow[];
  skills: readonly SkillRow[];
};
export type ManageableScopes = {
  manageableSpaceIds: ReadonlySet<string>;
  manageableAppIds: ReadonlySet<string>;
  manageableSkillIds: ReadonlySet<string>;
};
export const matchKey = (match: Match) => `${match.scopeKind}:${match.scopeId}`;

function scopeRoleOptions(customRoles: readonly CustomRole[]) {
  return {
    space: rolesForScope("space", [...customRoles]),
    connected_app: rolesForScope("connected_app", [...customRoles]),
    skill: rolesForScope("skill", [...customRoles]),
  } satisfies Record<ManageableKind, RoleOption[]>;
}

export function buildScopeIndex({
  spaces,
  apps,
  skills,
}: ScopeCatalog): Map<string, Scope> {
  const m = new Map<string, Scope>();
  for (const s of spaces)
    m.set(s.id, {
      scopeKind: "space",
      name: s.name,
      mode: s.mode,
      grants: s.roleGrantsAsScope,
    });
  for (const a of apps)
    m.set(a.id, {
      scopeKind: "connected_app",
      name: a.name,
      identifier: a.identifier,
      grants: a.roleGrantsAsScope,
    });
  for (const sk of skills)
    m.set(sk.id, {
      scopeKind: "skill",
      name: sk.name,
      grants: sk.roleGrantsAsScope,
    });
  return m;
}

export function pendingScopeSelections(
  staged: readonly StagedScope[],
  scopeById: ReadonlyMap<string, Scope>,
  principal: AccessPrincipal
) {
  return staged.filter((s) => {
    if (s.roles.length === 0) return true;
    const scope = scopeById.get(s.scopeId);
    if (!scope) return true;
    const held = scope.grants.filter((g) => isPrincipalGrant(g, principal));
    return s.roles.some(
      (role) =>
        !held.some(
          (g) =>
            (role.builtinRoleId != null &&
              g.builtinRoleId === role.builtinRoleId) ||
            (role.customRoleId != null && g.customRoleId === role.customRoleId)
        )
    );
  });
}

export function buildScopeAccessRows({
  principal,
  directGrants,
  inheritedGrants,
  scopeById,
  customRoles,
  resolveScopeName,
  personalSpaceId,
  canManage,
  manageableSpaceIds,
  manageableAppIds,
  manageableSkillIds,
}: ManageableScopes & {
  principal: AccessPrincipal;
  directGrants: readonly PrincipalGrant[];
  inheritedGrants: readonly InheritedGrant[];
  scopeById: ReadonlyMap<string, Scope>;
  customRoles: readonly CustomRole[];
  resolveScopeName: ResolveScopeName;
  personalSpaceId?: string;
  canManage: boolean;
}) {
  const rolesMap = new Map(customRoles.map((r) => [r.id, r]));
  const roleOptions = scopeRoleOptions(customRoles);
  const directByScope = new Map<string, PrincipalGrant[]>();
  for (const g of directGrants) {
    const arr = directByScope.get(g.scopeId);
    if (arr) arr.push(g);
    else directByScope.set(g.scopeId, [g]);
  }
  const inheritedByScope = new Map<string, InheritedGrant[]>();
  for (const g of inheritedGrants) {
    const arr = inheritedByScope.get(g.scopeId);
    if (arr) arr.push(g);
    else inheritedByScope.set(g.scopeId, [g]);
  }
  const manageableIds = {
    space: manageableSpaceIds,
    connected_app: manageableAppIds,
    skill: manageableSkillIds,
  } satisfies Record<ManageableKind, ReadonlySet<string>>;

  const rows: Row[] = [];
  const hidden: Record<ManageableKind, number> = {
    space: 0,
    connected_app: 0,
    skill: 0,
  };
  const scopeIds = new Set([
    ...directByScope.keys(),
    ...inheritedByScope.keys(),
  ]);
  for (const scopeId of scopeIds) {
    if (scopeId === personalSpaceId) continue;
    const direct = directByScope.get(scopeId) ?? [];
    const inherited = inheritedByScope.get(scopeId) ?? [];
    const sample = direct[0] ?? inherited[0];
    if (!sample) continue;
    const kind = inferScopeKind(sample, rolesMap);
    if (!isEditableScopeKind(kind)) continue;
    const scope = scopeById.get(scopeId);
    if (scope?.mode === "personal") continue;
    const name = scope ? resolveScopeName(scopeId, kind) : null;
    if (!scope || name == null) {
      hidden[kind]++;
      continue;
    }
    const rowGrants: GrantEntry[] = direct.map((g) => ({
      ...principal,
      builtinRoleId: g.builtinRoleId ?? undefined,
      customRoleId: g.customRoleId ?? undefined,
    }));
    const chipByKey = new Map<string, RoleChip>();
    const viaGroups = new Map<string, string>();
    for (const g of inherited) {
      const chip = grantToRoleChip({
        builtinRoleId: g.builtinRoleId,
        customRole: g.customRole,
      });
      if (chip && !chipByKey.has(chip.key)) chipByKey.set(chip.key, chip);
      viaGroups.set(g.viaGroupId, g.viaGroupName);
    }
    rows.push({
      scopeId,
      scopeKind: kind,
      name,
      mode: scope.mode,
      identifier: scope.identifier,
      directGrants: rowGrants,
      directRoleLabels: rowGrants
        .map((g) => roleLabel(g, customRoles as CustomRole[]))
        .sort(),
      roleOptions: roleOptions[kind],
      editable: canManage && manageableIds[kind].has(scopeId),
      inheritedChips: [...chipByKey.values()],
      viaGroups: [...viaGroups].map(([id, name]) => ({ id, name })),
    });
  }
  rows.sort((a, b) => {
    const kindDiff = SCOPE_ORDER[a.scopeKind] - SCOPE_ORDER[b.scopeKind];
    if (kindDiff !== 0) return kindDiff;
    return a.name.localeCompare(b.name);
  });
  return { rows, hidden };
}

export function findScopeMatches({
  spaces,
  apps,
  skills,
  search,
  directScopeIds,
  personalSpaceId,
  manageableSpaceIds,
  manageableAppIds,
  manageableSkillIds,
}: ScopeCatalog &
  ManageableScopes & {
    search: string;
    directScopeIds: ReadonlySet<string>;
    personalSpaceId?: string;
  }) {
  const lower = search.trim().toLowerCase();
  const matchesQuery = (label: string) =>
    !lower || label.toLowerCase().includes(lower);
  const spaceMatches = spaces.filter(
    (s) =>
      manageableSpaceIds.has(s.id) &&
      s.mode !== "personal" &&
      s.id !== personalSpaceId &&
      !directScopeIds.has(s.id) &&
      matchesQuery(s.name)
  );
  const appMatches = apps.filter(
    (a) =>
      manageableAppIds.has(a.id) &&
      !directScopeIds.has(a.id) &&
      matchesQuery(a.name)
  );
  const skillMatches = skills.filter(
    (sk) =>
      manageableSkillIds.has(sk.id) &&
      !directScopeIds.has(sk.id) &&
      matchesQuery(sk.name)
  );
  const flatMatches: Match[] = [
    ...spaceMatches.map((s) => ({
      scopeId: s.id,
      scopeKind: "space" as const,
    })),
    ...appMatches.map((a) => ({
      scopeId: a.id,
      scopeKind: "connected_app" as const,
    })),
    ...skillMatches.map((sk) => ({
      scopeId: sk.id,
      scopeKind: "skill" as const,
    })),
  ];
  return { spaceMatches, appMatches, skillMatches, flatMatches };
}

export function stageScopeMatches(
  matches: readonly Match[],
  scopeById: ReadonlyMap<string, Scope>,
  customRoles: readonly CustomRole[]
): StagedScope[] {
  const options = scopeRoleOptions(customRoles);
  return matches.flatMap((match) => {
    const scope = scopeById.get(match.scopeId);
    if (!scope) return [];
    const roleOptions = options[match.scopeKind];
    const defaultRole = defaultRoleForScope(match.scopeKind, roleOptions);
    return [
      {
        key: matchKey(match),
        ...match,
        name: scope.name,
        mode: scope.mode,
        identifier: scope.identifier,
        roleOptions,
        roles: defaultRole ? [defaultRole] : [],
      },
    ];
  });
}
